import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import * as _ from 'lodash';

import { GfMarkdownEditorComponent } from '../../../widgets/gf-markdown-editor/gf-markdown-editor.component';
import { WikiService } from '../../../widgets/wiki/services/wiki.service';
import { UserService } from '../../../core/services/core.user.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';

/** Bodies longer than this start collapsed behind a "Show more" link. */
const LONG_COMMENT_LENGTH = 400;

/** The comment box currently open: a new top-level comment, a reply to a comment, or an edit of one. */
interface CommentEditor {
    mode: 'new' | 'reply' | 'edit';
    targetId: number;
    body: string;
}

/** A comment and the replies made directly to it, each with their own replies. */
interface CommentNode {
    comment: any;
    replies: CommentNode[];
    /** Every reply below this comment, at any depth. */
    replyCount: number;
}

/**
 * Comment thread for a project wiki page, shown below the page.
 *
 * Comments are forum messages in the page's comment thread (see WikiService::getCommentthread on the backend).
 * The flat message list is built into a tree by parent message: each reply sits below and indented from the
 * comment it answers, and siblings at every level follow the chosen sort order.  The thread is only created once
 * someone opens the comment box, so viewing a page never writes.
 */
@Component({
    selector: 'wiki-comments',
    template: require('./wiki.comments.component.html'),
    styles: [`
        .wiki-comments-list { padding: 0 10px; }
        .wiki-comments-toolbar { padding: 6px 10px 0; text-align: right; }
        .wiki-comments-toolbar select { display: inline-block; width: auto; height: 26px; padding: 2px 6px; }
        .wiki-comment { display: flex; padding: 10px 0; }
        .wiki-comment-thread + .wiki-comment-thread { border-top: 1px solid #eee; }
        .wiki-comment-replies { margin-left: 40px; }
        .wiki-comment-replies .wiki-comment-replies { margin-left: 32px; }
        .wiki-comment-replies .wiki-comment { padding: 6px 0; }
        .wiki-comment-avatar { flex: none; margin-right: 8px; }
        .wiki-comment-main { flex: 1; min-width: 0; }
        .wiki-comment-content { min-height: 46px; }
        .wiki-comment-meta { font-size: 0.9em; }
        .wiki-comment-meta .timestamp { color: #999; margin-left: 4px; }
        .wiki-comment-body { overflow-wrap: anywhere; }
        .wiki-comment-body ::ng-deep img { max-width: 100%; height: auto; border: 1px solid #ddd; border-radius: 3px; }
        .wiki-comment-body.collapsed { max-height: 9em; overflow: hidden;
            -webkit-mask-image: linear-gradient(to bottom, #000 60%, transparent);
            mask-image: linear-gradient(to bottom, #000 60%, transparent); }
        .wiki-comment-actions { font-size: 0.9em; }
        .wiki-comment-actions a { cursor: pointer; margin-right: 10px; }
        .wiki-comments-compose { padding: 10px; border-bottom: 1px solid #ddd; }
        .wiki-comments-compose .form-control { cursor: text; }
        .wiki-comment-editor { margin: 6px 0; }
        .wiki-comment-editor .btn { margin: 6px 6px 0 0; }
    `]
})
export class WikiCommentsComponent implements OnChanges {

    @Input() wikiPage: any = null;
    @Input() project: any = null;

    /** The open comment box; only one is open at a time. */
    @ViewChild('commentEditor', { static: false }) commentEditor?: GfMarkdownEditorComponent;

    protected loading = false;
    protected saving = false;
    protected threads: CommentNode[] = [];
    protected comments: any[] = [];
    protected editor: CommentEditor | null = null;
    /** Comment whose Delete button was clicked once; a second click deletes it. */
    protected confirmDeleteId = 0;
    protected sortOrder: 'desc' | 'asc' = 'desc';

    /** Message ids whose long body the user expanded, and threads whose replies the user collapsed. */
    protected expanded: { [id: number]: boolean } = {};
    protected repliesHidden: { [id: number]: boolean } = {};

    protected hasUser = false;
    private currentUser: any = null;
    private isSiteAdmin = false;

    private thread: any = null;
    private byId: { [id: number]: any } = {};
    /** Bumped on every page change, so a slow response for a previous page is ignored. */
    private loadSeq = 0;

    constructor(
        private wikiService: WikiService,
        private userService: UserService,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService,
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes.wikiPage) {
            return;
        }
        if (_.get(changes.wikiPage.previousValue, 'id') === this.pageId && !changes.wikiPage.firstChange) {
            return;
        }

        this.hasUser = this.userService.session.hasUser();
        this.currentUser = this.userService.session.getUser();
        this.isSiteAdmin = this.hasUser && this.userService.session.isSiteAdmin();

        this.thread = null;
        this.editor = null;
        this.confirmDeleteId = 0;
        this.setComments([]);
        this.load();
    }

    get pageId(): number {
        return _.get(this.wikiPage, 'id', 0);
    }

    /** Forum the comments belong to; pasted images upload against it. */
    get forumId(): number {
        return _.get(this.thread, 'forum.id', 0);
    }

    get count(): number {
        return this.comments.length;
    }

    private load(): Promise<any> {
        const seq = ++this.loadSeq;
        if (!this.pageId) {
            return Promise.resolve();
        }

        this.loading = true;
        return this.wikiService.getCommentThread(this.pageId)
            .then((thread: any) => {
                if (seq !== this.loadSeq) { return; }
                this.thread = thread;
                return this.wikiService.getComments(thread.id).then((comments: any) => {
                    if (seq !== this.loadSeq) { return; }
                    this.setComments(comments || []);
                });
            })
            .catch((error) => {
                // 404 just means nobody has commented on this page yet.
                if (seq === this.loadSeq && _.get(error, 'status') !== 404) {
                    this.uiService.message.showError(this.gfTranslateService.translate('Could not load comments'));
                }
            })
            .finally(() => {
                if (seq === this.loadSeq) { this.loading = false; }
            });
    }

    private setComments(comments: any[]) {
        this.comments = comments;
        this.byId = _.keyBy(comments, 'id');
        this.buildThreads();
    }

    setSortOrder(order: 'desc' | 'asc') {
        this.sortOrder = order;
        this.buildThreads();
    }

    /**
     * Build the comment tree in the current sort order, which applies to the top-level comments and to the
     * replies under each comment alike.
     *
     * Deleting a comment does not delete its replies, so a reply whose parent is gone is placed under a
     * "deleted" placeholder standing in for it.  The deleted comment's own parent is not known, so the
     * placeholder sits at the top level.
     */
    private buildThreads() {
        const nodes: { [id: number]: CommentNode } = {};
        for (const comment of this.comments) {
            nodes[comment.id] = { comment: comment, replies: [], replyCount: 0 };
            const parentId = comment.parentForumMessageId;
            if (parentId > 0 && !this.byId[parentId] && !nodes[parentId]) {
                nodes[parentId] = { comment: { id: parentId, deleted: true }, replies: [], replyCount: 0 };
            }
        }

        this.threads = [];
        for (const node of _.orderBy(_.values(nodes), 'comment.id', this.sortOrder)) {
            const parent = nodes[node.comment.parentForumMessageId];
            // A reply is always newer than its parent; checking that also keeps a malformed parent chain from
            // forming a loop, which would drop the comments in it from the tree.
            if (parent && parent.comment.id < node.comment.id) {
                parent.replies.push(node);
            } else {
                this.threads.push(node);
            }
        }

        const countReplies = (node: CommentNode): number =>
            node.replyCount = _.sumBy(node.replies, (reply) => 1 + countReplies(reply));
        this.threads.forEach(countReplies);
    }

    /** The top-level comment a message sits under, or the placeholder for a deleted one. */
    private rootId(comment: any): number {
        let current = comment;
        // Guard against a malformed parent chain looping forever.
        for (let depth = 0; current.parentForumMessageId && depth < 100; depth++) {
            const parent = this.byId[current.parentForumMessageId];
            if (!parent) {
                return current.parentForumMessageId;
            }
            current = parent;
        }
        return current.id;
    }

    isLong(comment: any): boolean {
        return (comment.body || '').length > LONG_COMMENT_LENGTH;
    }

    /** Mirrors ForumMessageService::userCanUpdate/userCanDelete: the author, or a site admin. */
    canModify(comment: any): boolean {
        return this.hasUser && (this.isSiteAdmin || _.get(comment, 'user.id') == _.get(this.currentUser, 'id'));
    }

    isEditing(mode: string, targetId: number = 0): boolean {
        return !!this.editor && this.editor.mode === mode && this.editor.targetId === targetId;
    }

    /** Open a comment box.  The thread is created first so pasted images have a forum to upload to. */
    openEditor(mode: 'new' | 'reply' | 'edit', target: any = null) {
        if (this.saving) {
            return;
        }
        this.confirmDeleteId = 0;
        this.ensureThread().then(() => {
            this.editor = {
                mode: mode,
                targetId: target ? target.id : 0,
                body: mode === 'edit' ? target.body : ''
            };
        }, () => {
            this.uiService.message.showError(this.gfTranslateService.translate('Could not start a comment'));
        });
    }

    closeEditor() {
        this.editor = null;
    }

    private ensureThread(): Promise<any> {
        if (this.thread) {
            return Promise.resolve(this.thread);
        }
        const seq = this.loadSeq;
        return this.wikiService.createCommentThread(this.pageId).then((thread: any) => {
            if (seq === this.loadSeq) {
                this.thread = thread;
            }
            return thread;
        });
    }

    submit() {
        const editor = this.editor;
        if (!editor || this.saving || !this.thread) {
            return;
        }
        // Take the text from the editor, not from the bound model: contentChange is debounced, so the
        // last keystrokes before clicking submit may not have been emitted yet.
        if (this.commentEditor) {
            editor.body = this.commentEditor.getMarkdown();
        }
        const body = (editor.body || '').trim();
        if (!body) {
            this.uiService.message.showError(this.gfTranslateService.translate('The comment cannot be blank'));
            return;
        }

        const data: any = { forumThread: this.thread.id, body: body };
        if (editor.mode === 'edit') {
            data.id = editor.targetId;
        } else {
            data.subject = 'Comment';
            data.parentForumMessageId = editor.mode === 'reply' ? editor.targetId : 0;
        }

        this.saving = true;
        this.wikiService.saveComment(data)
            .then(() => {
                this.editor = null;
                if (editor.mode === 'reply') {
                    this.repliesHidden[this.rootId(this.byId[editor.targetId])] = false;
                }
                return this.load();
            })
            .catch(() => {
                this.uiService.message.showError(this.gfTranslateService.translate('Could not save the comment'));
            })
            .finally(() => this.saving = false);
    }

    /** The first click arms the Delete button (it turns red and asks "Delete?"); the second deletes. */
    deleteComment(comment: any) {
        if (this.saving) {
            return;
        }
        if (this.confirmDeleteId !== comment.id) {
            this.confirmDeleteId = comment.id;
            return;
        }
        this.saving = true;
        this.wikiService.deleteComment(comment.id)
            .then(() => {
                this.confirmDeleteId = 0;
                return this.load();
            })
            .catch(() => {
                this.uiService.message.showError(this.gfTranslateService.translate('Could not delete the comment'));
            })
            .finally(() => this.saving = false);
    }
}
